﻿/** @jsxImportSource jsx-dom/min */
import { DataGrid, GridPersistanceFlags, PersistedGridSettings } from "@serenity-is/corelib";
import { getGlobalThis, localText, trimToNull } from "@serenity-is/corelib/q";

export type CardViewType = "card" | "grid";

export interface CardViewMixinOptions<TItem> {
    grid: DataGrid<TItem, any>;
    /* This can also return a React JSX VDOM but in that case ReactDOM 
    should be available in the global namespace */
    renderItem: (item: TItem, index: number) => HTMLElement | any;
    viewType?: CardViewType;
    cardClass?: string;
}

/**
 * A mixin that can be applied to a DataGrid for card view functionality
 */
export class CardViewMixin<TItem> {

    private dataGrid: DataGrid<TItem, any>;
    private cardContainer: JQuery;

    constructor(private options: CardViewMixinOptions<TItem>) {
        var dg = this.dataGrid = options.grid;

        var btnGroup = $(
            <div class="btn-group view-switch" role="group" style="float: right">
                <a href="javascript:;" title={localText('Site.CardViewMixin.ListView')}
                    class="btn btn-primary active" data-view="list">
                    <i class="fa fa-list"></i>
                </a>
                <a href="javascript:;" title={localText('Site.CardViewMixin.CardView')}
                    class="btn btn-primary" data-view="card">
                    <i class="fa fa-grip-horizontal"></i>
                </a>
            </div>
        ).prependTo(dg.element.find('.grid-title'));

        this.cardContainer = $('<div class="card-container" style="display: none;"></div>')
            .insertAfter(dg.element.children('.grid-container'));

        btnGroup.find('[data-view]').click((e) => {
            this.switchView($(e.target).closest('[data-view]').attr('data-view') as CardViewType);
        });

        if (options.viewType)
            this.switchView(this.options.viewType, false);

        this.resizeCardView();

        dg.element.bind('layout', () => this.resizeCardView());

        (dg.view as any).onDataChanged.subscribe(() => {
            this.updateCardItems();
        });

        var oldCurrentSettings = (dg as any).getCurrentSettings;
        (dg as any).getCurrentSettings = function (flag: GridPersistanceFlags) {
            var settings = oldCurrentSettings.apply(dg, [flag]);
            settings['viewType'] = btnGroup.find('[data-view].active').val();
            return settings;
        };

        var self = this;
        var oldRestoreSettings = (dg as any).restoreSettings;
        (dg as any).restoreSettings = function (settings?: PersistedGridSettings, flags?: GridPersistanceFlags) {
            oldRestoreSettings.apply(dg, [settings, flags]);

            if (settings == null) {
                var storage = this.getPersistanceStorage();
                if (storage == null)
                    return;

                var json = trimToNull(storage.getItem(this.getPersistanceKey()));
                if (!json)
                    return;

                settings = JSON.parse(json);
            }

            var viewType = (settings as any).viewType;
            if (viewType == "card" ||
                viewType == "list")
                self.switchView(viewType, false);
        };
    }

    public switchView(viewType: CardViewType, persist: boolean = true) {

        var button = this.dataGrid.element
            .find('.btn[data-view="' + viewType + '"]');

        button.addClass('active').siblings('.active').not(button).removeClass('active');

        var card = viewType == 'card';
        this.dataGrid.element.children('.card-container').toggle(card);
        this.dataGrid.element.children('.grid-container').toggle(!card);

        if (card)
            this.updateCardItems();

        if (persist)
            (this.dataGrid as any).persistSettings();

        this.dataGrid.element.trigger('layout');
    }

    private updateCardItems() {
        const colClass = this.options.cardClass ?? "col-12 col-md-6 col-lg-6 col-xxl-4";

        $(this.cardContainer[0]).html('').append(
            <div className="row card-items">
                {this.dataGrid.getItems().map((item, index) =>
                    <div className={colClass} key={index}>
                        <div className="card" ref={el => {
                            var node = this.options.renderItem(item, index);
                            if (node instanceof HTMLElement) {
                                el.appendChild(node);
                            }
                            else {
                                getGlobalThis()?.ReactDOM.render(node as any, el);
                            }
                        }}></div>
                    </div>
                )}
            </div>
        );
    }

    private resizeCardView() {
        var gc = this.dataGrid.element.children('.grid-container');
        if (!gc.is(':visible'))
            return;
        var height = gc.height();
        if (height) {
            this.dataGrid.element.children('.card-container').css({
                height: height + 'px'
            });
        }
    }
}

if (typeof globalThis !== "undefined" && globalThis.Q && globalThis.Q.initFullHeightGridPage) {
    globalThis.Q.initFullHeightGridPage = function (gridDiv: JQuery) {
        $('body').addClass('full-height-page');
        gridDiv.addClass('responsive-height');
        var layout = function () {
            var inPageContent = gridDiv.parent().hasClass('page-content') ||
                gridDiv.parent().is('section.content');
            if (inPageContent) {
                gridDiv.css('height', '1px').css('overflow', 'hidden');
            }
            var cc = gridDiv.children('.card-container');
            if (cc.length && cc.is(':visible')) {
                cc.hide();
                gridDiv.children('.grid-container').show();
                try {
                    globalThis.Q.layoutFillHeight(gridDiv);
                    gridDiv.triggerHandler('layout');
                }
                finally {
                    gridDiv.children('.grid-container').hide();
                    cc.show();
                }
            }
            else {
                globalThis.Q.layoutFillHeight(gridDiv);
                if (inPageContent) {
                    gridDiv.css('overflow', '');
                }
                gridDiv.triggerHandler('layout');
            }
        };
        if ($('body').hasClass('has-layout-event')) {
            $('body').bind('layout', layout);
            gridDiv.one('remove', function () {
                $(window).off('layout', layout);
                $('body').off('layout', layout);
            });
        }
        else if ((window as any).Metronic) {
            (window as any).Metronic.addResizeHandler(layout);
        }
        else {
            $(window).resize(layout);
        }
        layout();
        globalThis.Q.Router?.resolve();
    }
}